import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { authApi, billingApi, validationApi } from "./api";
import {
  HelpPanel,
  FaqPanel,
  ReleaseNotesPanel,
  KeyboardShortcutsPanel,
  TermsPanel,
  PrivacyPanel,
  ReportBugPanel,
} from "./HelpPages";
import AdminPanel from "./AdminPanel";
import LibraryPanel, { IconStar } from "./LibraryPanel";
import LandingPage from "./LandingPage";
import PaymentModal from "./PaymentModal";
import IdeaDevelopmentPanel from "./IdeaDevelopmentPanel";

const APP_NAME = "AI Startup Validator";

const COLORS = {
  bg: "#080C18",
  surface: "rgba(255,255,255,0.025)",
  border: "rgba(255,255,255,0.07)",
  indigo: "#6366F1",
  violet: "#7C3AED",
  text: "#F8F7FF",
  muted: "rgba(148,163,184,0.7)",
  green: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
};

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "$9",
    period: "/mo",
    credits: "50 validations/mo",
    color: "#6366F1",
    features: [
      "50 validations/month",
      "All 5 score dimensions",
      "Bull & Bear analysis",
      "ICP Generator (Layer 2)",
      "Email support",
    ],
    cta: "Start Free Trial",
    popular: false,
    priceCents: 900,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$29",
    period: "/mo",
    credits: "Unlimited",
    color: "#8B5CF6",
    features: [
      "Unlimited validations",
      "Full Layer 2 toolkit",
      "Competitor Mapper & Pitch Deck",
      "Landing Page Generator",
      "PDF export & API access",
    ],
    cta: "Go Pro",
    popular: true,
    priceCents: 2900,
  },
  {
    id: "team",
    name: "Team",
    price: "$99",
    period: "/mo",
    credits: "5 seats",
    color: "#10B981",
    features: [
      "5 team seats",
      "Unlimited validations",
      "White-label reports",
      "Slack integration",
      "Dedicated CSM",
    ],
    cta: "Start Team Trial",
    popular: false,
    priceCents: 9900,
  },
];

const EXAMPLE_CHIPS = [
  "Uber for dog walks",
  "AI lawyer for small claims",
  "LinkedIn for Gen Z",
  "Carbon credits marketplace",
];

const PHASE_LABELS = [
  "Reading your idea…",
  "Sizing the market…",
  "Checking competition…",
  "Calculating scores…",
  "Writing verdict…",
];

const SCORE_DIMENSIONS = [
  { key: "market", label: "Market", icon: "📊" },
  { key: "timing", label: "Timing", icon: "⏱" },
  { key: "competition", label: "Competition", icon: "⚔️" },
  { key: "monetization", label: "Monetization", icon: "💰" },
  { key: "moat", label: "Moat", icon: "🏰" },
];

const SIDEBAR_WIDTH = 260;
const HISTORY_KEY = "vcscore_history";
const SETTINGS_KEY = "asv_settings";
const DEMO_USES_KEY = "asv_demo_uses";
const DEMO_LIMIT = 3;

function getDemoUses() {
  try {
    const n = Number(localStorage.getItem(DEMO_USES_KEY) || 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function incrementDemoUses() {
  const next = getDemoUses() + 1;
  try {
    localStorage.setItem(DEMO_USES_KEY, String(next));
  } catch {
    /* ignore */
  }
  return next;
}

function demoUsesRemaining() {
  return Math.max(0, DEMO_LIMIT - getDemoUses());
}

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? JSON.parse(raw) : { showExamples: true, emailUpdates: false };
  } catch {
    return { showExamples: true, emailUpdates: false };
  }
}

function saveSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
}

function makeSessionId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const IDEA_PREFIX_PATTERNS = [
  /^my idea is to (?:create|build|make|start|launch|develop)\s+/i,
  /^i want to (?:create|build|make|start|launch|develop)\s+/i,
  /^i(?:'m| am) (?:building|creating|starting|launching|developing)\s+(?:a|an|the)\s+/i,
  /^i(?:'m| am) (?:building|creating|starting|launching|developing)\s+/i,
  /^idea:\s*/i,
];

function capitalizeTitle(text) {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function truncateTitleAtWord(text, maxLen) {
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(" ");
  const base = lastSpace > 20 ? cut.slice(0, lastSpace) : cut;
  return `${base.trim()}…`;
}

function deriveTitleFromIdea(idea) {
  let trimmed = String(idea || "").trim();
  if (!trimmed) return "New validation";

  for (const pattern of IDEA_PREFIX_PATTERNS) {
    trimmed = trimmed.replace(pattern, "");
  }
  trimmed = trimmed.trim();
  if (!trimmed) return "New validation";

  const firstSentence = trimmed.split(/[.!?\n]/)[0].trim();
  const base = firstSentence || trimmed;
  return capitalizeTitle(truncateTitleAtWord(base, 48));
}

function computeSessionTitle(session, updates = {}) {
  if (updates.title) return updates.title;

  const result = updates.result !== undefined ? updates.result : session.result;
  const idea = updates.idea !== undefined ? updates.idea : session.idea;

  if (result?.title) return result.title;
  if (result && session.title && session.title !== "New validation") return session.title;
  if (result) return deriveTitleFromIdea(idea);
  return deriveTitleFromIdea(idea);
}

function createSession(idea = "") {
  const now = Date.now();
  return {
    id: makeSessionId(),
    title: deriveTitleFromIdea(idea),
    idea,
    result: null,
    starred: false,
    createdAt: now,
    updatedAt: now,
  };
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(sessions) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(sessions.slice(0, 50)));
  } catch {
    /* ignore quota errors */
  }
}

function groupSessionsByDate(sessions) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86400000;
  const startOfWeek = startOfToday - 6 * 86400000;

  const groups = [
    { label: "Today", items: [] },
    { label: "Yesterday", items: [] },
    { label: "Previous 7 days", items: [] },
    { label: "Older", items: [] },
  ];

  [...sessions]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .forEach((session) => {
      const t = session.updatedAt;
      if (t >= startOfToday) groups[0].items.push(session);
      else if (t >= startOfYesterday) groups[1].items.push(session);
      else if (t >= startOfWeek) groups[2].items.push(session);
      else groups[3].items.push(session);
    });

  return groups.filter((g) => g.items.length > 0);
}

function IconPlus({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function IconSidebar({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M9 3v18" />
    </svg>
  );
}

function IconMessage({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconTrash({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
    </svg>
  );
}

function IconMenu({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function IconChevronRight({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function IconSparkle({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z" />
      <path d="M5 3v3M3 5h3M19 19v3M21 19h-3" />
    </svg>
  );
}

function IconUserCircle({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="10" r="3" />
      <path d="M6.5 18.5c.9-2.2 3-3.5 5.5-3.5s4.6 1.3 5.5 3.5" />
    </svg>
  );
}

function IconSettings({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function IconHelp({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
    </svg>
  );
}

function IconHelpCenter({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 4.5 1.5c0 1.5-2 1.5-2 3" />
      <circle cx="12" cy="17" r="0.5" fill="currentColor" />
    </svg>
  );
}

function IconBook({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H6.5A2.5 2.5 0 0 0 4 21.5V5.5z" />
      <path d="M4 5.5A2.5 2.5 0 0 0 6.5 3H20" />
    </svg>
  );
}

function IconPenLine({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

function IconKeyboard({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" />
    </svg>
  );
}

function IconFileText({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M8 13h8M8 17h8M8 9h2" />
    </svg>
  );
}

function IconInfoCircle({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 10v6M12 7h.01" />
    </svg>
  );
}

function IconBug({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <path d="M8 2v2M16 2v2M12 2v2" />
      <path d="M4 8h16M6 12h12M8 16h8" />
      <path d="M9 22h6M12 6v14" />
      <path d="M4 8l-2 2M20 8l2 2M4 16l-2-2M20 16l2-2" />
    </svg>
  );
}

function IconLogOut({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

function IconShield({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />
    </svg>
  );
}

const AVATAR_COLORS = ["#10B981", "#6366F1", "#8B5CF6", "#F59E0B", "#EC4899", "#14B8A6"];

function avatarColor(name = "") {
  if (!name || name.trim().toLowerCase() === "guest") {
    return COLORS.indigo;
  }
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function userInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function planLabel(plan) {
  if (!plan || plan === "free") return "Free";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

function UserAvatar({ name, size = 32 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: avatarColor(name),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.36,
        fontWeight: 600,
        color: "#fff",
        flexShrink: 0,
        letterSpacing: "-0.02em",
      }}
    >
      {userInitials(name)}
    </div>
  );
}

function UserMenuItem({ icon, label, onClick, showChevron, danger, active, onMouseEnter }) {
  return (
    <button
      type="button"
      className="user-menu-item"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "10px 12px",
        border: "none",
        borderRadius: 10,
        background: active ? "rgba(255,255,255,0.08)" : "transparent",
        color: danger ? COLORS.red : COLORS.text,
        fontSize: 14,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ color: COLORS.muted, display: "flex", flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {showChevron && <IconChevronRight size={14} />}
    </button>
  );
}

const HELP_SUBMENU_ITEMS = [
  {
    id: "help-center",
    label: "Help center",
    icon: IconHelpCenter,
    action: (handlers) => handlers.openPanel("help"),
  },
  {
    id: "faq",
    label: "FAQ",
    icon: IconBook,
    action: (handlers) => handlers.openPanel("faq"),
  },
  {
    id: "release-notes",
    label: "Release notes",
    icon: IconPenLine,
    action: (handlers) => handlers.openPanel("release-notes"),
  },
  {
    id: "shortcuts",
    label: "Keyboard shortcuts",
    icon: IconKeyboard,
    action: (handlers) => handlers.openPanel("shortcuts"),
  },
  { type: "divider" },
  {
    id: "terms",
    label: "Terms of Service",
    icon: IconFileText,
    action: (handlers) => handlers.openPanel("terms"),
  },
  {
    id: "privacy",
    label: "Privacy Policy",
    icon: IconInfoCircle,
    action: (handlers) => handlers.openPanel("privacy"),
  },
  {
    id: "bug",
    label: "Report a bug",
    icon: IconBug,
    action: (handlers) => handlers.openPanel("report-bug"),
  },
];

function HelpSubmenu({ position, onOpenPanel, menuRef, onItemSelect }) {
  const handlers = { openPanel: onOpenPanel };
  const [coords, setCoords] = useState(() => ({
    top: Math.max(8, position.anchorBottom - 310),
    left: position.left,
  }));

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;

    const height = el.offsetHeight;
    const pad = 8;
    const viewportH = window.innerHeight;

    let top = position.anchorBottom - height;
    if (top < pad) top = pad;
    if (top + height > viewportH - pad) top = viewportH - pad - height;

    setCoords({ top, left: position.left });
  }, [position, menuRef]);

  const runAction = (item) => {
    item.action(handlers);
    onItemSelect?.();
  };

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="Help submenu"
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        minWidth: 220,
        maxHeight: "calc(100vh - 16px)",
        overflowY: "auto",
        background: "rgba(20, 27, 46, 0.78)",
        backdropFilter: "blur(16px) saturate(160%)",
        WebkitBackdropFilter: "blur(16px) saturate(160%)",
        border: `1px solid rgba(255, 255, 255, 0.1)`,
        borderRadius: 16,
        padding: 6,
        boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
        animation: "modalPop 0.15s ease",
        zIndex: 10001,
        pointerEvents: "auto",
      }}
    >
      {HELP_SUBMENU_ITEMS.map((item) =>
        item.type === "divider" ? (
          <div key="divider" style={{ height: 1, background: COLORS.border, margin: "4px 8px" }} />
        ) : (
          <UserMenuItem
            key={item.id}
            icon={<item.icon />}
            label={item.label}
            onClick={() => runAction(item)}
          />
        )
      )}
    </div>
  );
}

function UserMenuFooter({ user, isPaid, credits, onUpgrade, onLogout, onLogin, onRegister, onOpenPanel }) {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const [helpPosition, setHelpPosition] = useState({ anchorTop: 0, anchorBottom: 0, left: 0 });
  const anchorRef = useRef(null);
  const menuPanelRef = useRef(null);
  const helpRef = useRef(null);
  const helpMenuRef = useRef(null);

  const updateMenuRect = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setMenuRect({
      left: rect.left,
      bottom: window.innerHeight - rect.top + 6,
      width: Math.max(rect.width, 220),
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setHelpOpen(false);
      return undefined;
    }

    updateMenuRect();
    const onLayout = () => updateMenuRect();
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);

    const onPointerDown = (e) => {
      const target = e.target;
      if (anchorRef.current?.contains(target)) return;
      if (menuPanelRef.current?.contains(target)) return;
      if (helpMenuRef.current?.contains(target)) return;
      setOpen(false);
      setHelpOpen(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener("pointerdown", onPointerDown);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
  }, [open, updateMenuRect]);

  const displayName = user?.name || "Guest";
  const subtitle = user ? planLabel(user.plan) : "Sign in";
  const showUpgrade = !user || !isPaid;

  const closeAll = useCallback(() => {
    setOpen(false);
    setHelpOpen(false);
  }, []);

  const closeAnd = (fn) => () => {
    closeAll();
    fn?.();
  };

  const openHelpSubmenu = () => {
    if (helpRef.current) {
      const rect = helpRef.current.getBoundingClientRect();
      const submenuWidth = 220;
      const gap = 6;
      let left = rect.right + gap;
      if (left + submenuWidth > window.innerWidth - 8) {
        left = Math.max(8, rect.left - submenuWidth - gap);
      }
      setHelpPosition({ anchorTop: rect.top, anchorBottom: rect.bottom, left });
    }
    setHelpOpen(true);
  };

  const toggleHelpSubmenu = (e) => {
    e.stopPropagation();
    if (helpOpen) {
      setHelpOpen(false);
      return;
    }
    openHelpSubmenu();
  };

  const handleHelpPanelOpen = (panel) => {
    closeAll();
    onOpenPanel(panel);
  };

  const toggleMenu = () => {
    if (open) {
      closeAll();
      return;
    }
    updateMenuRect();
    setOpen(true);
  };

  const helpMenuItem = (
    <div ref={helpRef}>
      <UserMenuItem
        icon={<IconHelp />}
        label="Help"
        showChevron
        active={helpOpen}
        onClick={toggleHelpSubmenu}
      />
    </div>
  );

  const menuDropdown =
    open && menuRect
      ? createPortal(
          <>
            <div
              ref={menuPanelRef}
              role="menu"
              aria-label="User menu"
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                left: menuRect.left,
                bottom: menuRect.bottom,
                width: menuRect.width,
                background: "rgba(20, 27, 46, 0.78)",
                backdropFilter: "blur(16px) saturate(160%)",
                WebkitBackdropFilter: "blur(16px) saturate(160%)",
                border: `1px solid rgba(255, 255, 255, 0.1)`,
                borderRadius: 16,
                padding: 6,
                boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
                animation: "modalPop 0.2s ease",
                zIndex: 10000,
                pointerEvents: "auto",
              }}
            >
              {user ? (
                <>
                  <button
                    type="button"
                    className="user-menu-item"
                    onClick={closeAnd(() => onOpenPanel("profile"))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      width: "100%",
                      padding: "10px 12px",
                      border: "none",
                      borderRadius: 10,
                      background: "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <UserAvatar name={user.name} size={28} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontWeight: 600, fontSize: 14, color: COLORS.text }}>{user.name}</span>
                      <span style={{ display: "block", fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                        {planLabel(user.plan)}
                        {!isPaid && ` · ${credits} left`}
                      </span>
                    </span>
                    <IconChevronRight size={14} />
                  </button>
                  <div style={{ height: 1, background: COLORS.border, margin: "4px 8px" }} />
                  <UserMenuItem icon={<IconSparkle />} label="Upgrade plan" onClick={closeAnd(onUpgrade)} />
                  <UserMenuItem icon={<IconUserCircle />} label="Profile" onClick={closeAnd(() => onOpenPanel("profile"))} />
                  <UserMenuItem icon={<IconSettings />} label="Settings" onClick={closeAnd(() => onOpenPanel("settings"))} />
                  {user.isAdmin && (
                    <UserMenuItem icon={<IconShield />} label="Admin" onClick={closeAnd(() => onOpenPanel("admin"))} />
                  )}
                  <div style={{ height: 1, background: COLORS.border, margin: "4px 8px" }} />
                  {helpMenuItem}
                  <UserMenuItem icon={<IconLogOut />} label="Log out" onClick={closeAnd(onLogout)} danger />
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="user-menu-item"
                    onClick={closeAll}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      width: "100%",
                      padding: "10px 12px",
                      border: "none",
                      borderRadius: 10,
                      background: "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <UserAvatar name="Guest" size={28} />
                    <span style={{ flex: 1 }}>
                      <span style={{ display: "block", fontWeight: 600, fontSize: 14, color: COLORS.text }}>Guest</span>
                      <span style={{ display: "block", fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                        Sign in to save history
                      </span>
                    </span>
                  </button>
                  <div style={{ height: 1, background: COLORS.border, margin: "4px 8px" }} />
                  <UserMenuItem icon={<IconUserCircle />} label="Log in" onClick={closeAnd(onLogin)} />
                  <UserMenuItem icon={<IconSparkle />} label="Sign up" onClick={closeAnd(onRegister)} />
                  <div style={{ height: 1, background: COLORS.border, margin: "4px 8px" }} />
                  {helpMenuItem}
                </>
              )}
            </div>
            {helpOpen && (
              <HelpSubmenu
                position={helpPosition}
                onOpenPanel={handleHelpPanelOpen}
                menuRef={helpMenuRef}
                onItemSelect={closeAll}
              />
            )}
          </>,
          document.body
        )
      : null;

  return (
    <div style={{ position: "relative", padding: "8px 10px 10px", flexShrink: 0, borderTop: `1px solid ${COLORS.border}` }}>
      {menuDropdown}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 2px",
        }}
      >
        <button
          ref={anchorRef}
          type="button"
          onClick={toggleMenu}
          aria-expanded={open}
          aria-haspopup="menu"
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "6px 8px",
            borderRadius: 10,
            border: "none",
            background: open ? "rgba(255,255,255,0.06)" : "transparent",
            cursor: "pointer",
            textAlign: "left",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
          onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = "transparent"; }}
        >
          <UserAvatar name={displayName} size={32} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </span>
            <span style={{ display: "block", fontSize: 12, color: COLORS.muted, marginTop: 1 }}>{subtitle}</span>
          </span>
        </button>

        {showUpgrade && (
          <button
            type="button"
            onClick={user ? onUpgrade : onRegister}
            style={{
              flexShrink: 0,
              padding: "7px 14px",
              borderRadius: 99,
              border: `1px solid rgba(255,255,255,0.2)`,
              background: "transparent",
              color: COLORS.text,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            Upgrade
          </button>
        )}
      </div>
    </div>
  );
}

const GLOBAL_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', sans-serif; background: ${COLORS.bg}; color: ${COLORS.text}; }

  @keyframes fadeUp   { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin     { to{transform:rotate(360deg)} }
  @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @keyframes blink    { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes shimmer  { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  @keyframes orbFloat { 0%,100%{transform:translateY(0) translateX(-50%)} 50%{transform:translateY(-40px) translateX(-50%)} }
  @keyframes verdictPop { 0%{transform:scale(0.7);opacity:0} 60%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }
  @keyframes modalPop { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} }
  @keyframes scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(400%)} }
  @keyframes slideInSidebar { from{transform:translateX(-100%)} to{transform:translateX(0)} }

  .sidebar-scroll::-webkit-scrollbar { width: 4px; }
  .sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 99px; }
  .sidebar-item:hover .sidebar-delete { opacity: 1; }
  .user-menu-item:hover { background: rgba(255,255,255,0.06); }

  #validate {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
  }
  @media (min-width: 768px) {
    #validate { max-width: 1040px; }
  }
`;

function getScoreColor(score) {
  if (score >= 75) return COLORS.green;
  if (score >= 50) return COLORS.amber;
  return COLORS.red;
}

function getVerdictColor(verdict) {
  if (verdict === "FUND IT") return COLORS.green;
  if (verdict === "NEEDS WORK") return COLORS.amber;
  return COLORS.red;
}

function getOverallScore(scores) {
  const vals = Object.values(scores);
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

function OrbBg() {
  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      <div
        style={{
          position: "absolute",
          top: "-10%",
          left: "50%",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)",
          filter: "blur(60px)",
          animation: "orbFloat 8s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-5%",
          right: "-5%",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)",
          filter: "blur(60px)",
          animation: "orbFloat 11s ease-in-out infinite reverse",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "10%",
          left: "-3%",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
          animation: "orbFloat 9s ease-in-out infinite",
        }}
      />
    </div>
  );
}

function BigRing({ score, color, animate }) {
  const [displayScore, setDisplayScore] = useState(0);
  const r = 54;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (displayScore / 100) * circumference;

  useEffect(() => {
    if (!animate) {
      setDisplayScore(0);
      return;
    }
    const start = performance.now();
    const duration = 1600;
    let frame;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      setDisplayScore(Math.round(eased * score));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [score, animate]);

  return (
    <svg width={140} height={140} style={{ flexShrink: 0 }}>
      <defs>
        <filter id="ringGlow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle cx={70} cy={70} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={8} />
      <circle
        cx={70}
        cy={70}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={8}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform="rotate(-90 70 70)"
        filter="url(#ringGlow)"
        style={{ transition: "stroke-dashoffset 0.1s linear" }}
      />
      <text x={70} y={66} textAnchor="middle" fill={COLORS.text} fontSize={28} fontWeight={700} fontFamily="Space Grotesk, sans-serif">
        {displayScore}
      </text>
      <text x={70} y={86} textAnchor="middle" fill={COLORS.muted} fontSize={10} fontWeight={500} letterSpacing={2}>
        SCORE
      </text>
    </svg>
  );
}

function ScoreBar({ score, label, icon, delay, animate }) {
  const color = getScoreColor(score);
  return (
    <div style={{ marginBottom: 16, animation: animate ? `fadeUp 0.5s ease ${delay}ms both` : undefined }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: 6,
              background: "rgba(255,255,255,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
            }}
          >
            {icon}
          </div>
          <span style={{ fontSize: 14, color: COLORS.text }}>{label}</span>
        </div>
        <span style={{ fontSize: 14, fontWeight: 600, color }}>{score}</span>
      </div>
      <div style={{ height: 5, borderRadius: 99, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            borderRadius: 99,
            background: color,
            width: animate ? `${score}%` : "0%",
            transition: `width 1s ease ${delay}ms`,
          }}
        />
      </div>
    </div>
  );
}

function TypingText({ text, speed = 14, onDone }) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    setDisplayed("");
    setDone(false);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(interval);
        setDone(true);
        onDoneRef.current?.();
      }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <span>
      {displayed}
      {!done && (
        <span style={{ animation: "blink 1s step-end infinite", color: COLORS.indigo }}>▌</span>
      )}
    </span>
  );
}

function LoadingCard() {
  const shimmer = "linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%)";
  const barStyle = (width) => ({
    height: 14,
    width,
    borderRadius: 8,
    background: shimmer,
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite",
    marginBottom: 12,
  });
  return (
    <div
      style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 16,
        padding: 24,
        marginBottom: 16,
      }}
    >
      <div style={barStyle("80%")} />
      <div style={barStyle("55%")} />
      <div style={{ ...barStyle("65%"), marginBottom: 0 }} />
    </div>
  );
}

function CreditPill({ credits, total = 3, onUpgrade, compact = false }) {
  const pct = credits / total;
  const ringColor = credits === 0 ? COLORS.red : credits === 1 ? COLORS.amber : COLORS.green;
  const r = 10;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct);

  if (compact) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg width={28} height={28}>
            <circle cx={14} cy={14} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={3} />
            <circle
              cx={14}
              cy={14}
              r={r}
              fill="none"
              stroke={ringColor}
              strokeWidth={3}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 14 14)"
              strokeLinecap="round"
            />
          </svg>
          <span style={{ fontSize: 13, color: COLORS.muted }}>
            {credits > 0 ? `${credits} free left` : "No credits left"}
          </span>
        </div>
        <button
          onClick={onUpgrade}
          style={{
            width: "100%",
            background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
            border: "none",
            borderRadius: 10,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            padding: "10px 14px",
            cursor: "pointer",
          }}
        >
          Upgrade
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 99,
        padding: "6px 6px 6px 12px",
      }}
    >
      <svg width={28} height={28}>
        <circle cx={14} cy={14} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={3} />
        <circle
          cx={14}
          cy={14}
          r={r}
          fill="none"
          stroke={ringColor}
          strokeWidth={3}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 14 14)"
          strokeLinecap="round"
        />
      </svg>
      <span style={{ fontSize: 13, color: COLORS.muted }}>
        {credits > 0 ? `${credits} free left` : "No credits left"}
      </span>
      <button
        onClick={onUpgrade}
        style={{
          background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
          border: "none",
          borderRadius: 99,
          color: "#fff",
          fontSize: 12,
          fontWeight: 600,
          padding: "6px 14px",
          cursor: "pointer",
        }}
      >
        Upgrade
      </button>
    </div>
  );
}

function Sidebar({
  open,
  onToggle,
  sessions,
  activeId,
  onSelect,
  onNew,
  onDelete,
  credits,
  isPaid,
  onUpgrade,
  isMobile,
  user,
  onLogin,
  onRegister,
  onLogout,
  onOpenPanel,
  onOpenLibrary,
  libraryFilter,
  onLibraryFilterChange,
  onToggleStar,
}) {
  const groups = groupSessionsByDate(
    libraryFilter === "starred" ? sessions.filter((s) => s.starred) : sessions
  );

  const sidebarContent = (
    <>
      <div style={{ padding: "14px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 14, fontWeight: 700, paddingLeft: 4, lineHeight: 1.3 }}>
          <span style={{ color: COLORS.indigo }}>AI</span> Startup Validator
        </div>
        <button
          onClick={onToggle}
          title="Close sidebar"
          style={{
            background: "transparent",
            border: "none",
            color: COLORS.muted,
            cursor: "pointer",
            padding: 8,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconSidebar />
        </button>
      </div>

      <div style={{ padding: "0 12px 12px", flexShrink: 0 }}>
        <button
          onClick={onNew}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "11px 14px",
            borderRadius: 10,
            border: `1px solid ${COLORS.border}`,
            background: "rgba(255,255,255,0.04)",
            color: COLORS.text,
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
        >
          <IconPlus />
          New validation
        </button>
        {user && (
          <button
            onClick={onOpenLibrary}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              marginTop: 8,
              borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              background: "rgba(99,102,241,0.08)",
              color: COLORS.indigo,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Browse library
          </button>
        )}
      </div>

      <div className="sidebar-scroll" style={{ flex: 1, overflowY: "auto", padding: "0 8px" }}>
        {user && (
          <div style={{ padding: "4px 12px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Idea library
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              {["all", "starred"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => onLibraryFilterChange(f)}
                  style={{
                    padding: "3px 8px",
                    borderRadius: 99,
                    border: `1px solid ${libraryFilter === f ? COLORS.indigo : COLORS.border}`,
                    background: libraryFilter === f ? "rgba(99,102,241,0.12)" : "transparent",
                    color: libraryFilter === f ? COLORS.text : COLORS.muted,
                    fontSize: 10,
                    cursor: "pointer",
                  }}
                >
                  {f === "all" ? "All" : "★"}
                </button>
              ))}
            </div>
          </div>
        )}
        {groups.length === 0 ? (
          <p style={{ fontSize: 13, color: COLORS.muted, padding: "8px 12px", lineHeight: 1.5 }}>
            {libraryFilter === "starred"
              ? "No favorites yet. Star ideas you like from the results."
              : user
                ? "Your validated ideas will appear here automatically."
                : "Your validation history will appear here."}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.label} style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: COLORS.muted,
                  padding: "4px 12px 8px",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {group.label}
              </div>
              {group.items.map((session) => {
                const isActive = session.id === activeId;
                return (
                  <div
                    key={session.id}
                    className="sidebar-item"
                    onClick={() => onSelect(session.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "9px 12px",
                      borderRadius: 10,
                      cursor: "pointer",
                      marginBottom: 2,
                      background: isActive ? "rgba(99,102,241,0.15)" : session.starred ? "rgba(245,158,11,0.06)" : "transparent",
                      border: isActive
                        ? `1px solid rgba(99,102,241,0.25)`
                        : session.starred
                          ? "1px solid rgba(245,158,11,0.2)"
                          : "1px solid transparent",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span style={{ color: session.starred ? "#F59E0B" : COLORS.muted, flexShrink: 0, display: "flex" }}>
                      {session.starred ? <IconStar filled size={14} /> : <IconMessage />}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 13,
                        color: isActive ? COLORS.text : COLORS.muted,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {session.title}
                    </span>
                    <button
                      className="sidebar-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleStar(session.id);
                      }}
                      title={session.starred ? "Unstar" : "Star"}
                      style={{
                        opacity: session.starred ? 1 : 0,
                        background: "none",
                        border: "none",
                        color: session.starred ? "#F59E0B" : COLORS.muted,
                        cursor: "pointer",
                        padding: 4,
                        borderRadius: 6,
                        display: "flex",
                        flexShrink: 0,
                        transition: "opacity 0.15s, color 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#F59E0B"; }}
                      onMouseLeave={(e) => { if (!session.starred) e.currentTarget.style.color = COLORS.muted; }}
                    >
                      <IconStar filled={session.starred} size={14} />
                    </button>
                    <button
                      className="sidebar-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(session.id);
                      }}
                      title="Delete"
                      style={{
                        opacity: 0,
                        background: "none",
                        border: "none",
                        color: COLORS.muted,
                        cursor: "pointer",
                        padding: 4,
                        borderRadius: 6,
                        display: "flex",
                        flexShrink: 0,
                        transition: "opacity 0.15s, color 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = COLORS.red; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = COLORS.muted; }}
                    >
                      <IconTrash />
                    </button>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <UserMenuFooter
        user={user}
        isPaid={isPaid}
        credits={credits}
        onUpgrade={onUpgrade}
        onLogout={onLogout}
        onLogin={onLogin}
        onRegister={onRegister}
        onOpenPanel={onOpenPanel}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        {open && (
          <div
            onClick={onToggle}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
              zIndex: 200,
              backdropFilter: "blur(2px)",
            }}
          />
        )}
        <aside
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: SIDEBAR_WIDTH,
            background: "#0A0F1C",
            borderRight: `1px solid ${COLORS.border}`,
            zIndex: 201,
            display: "flex",
            flexDirection: "column",
            transform: open ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 0.25s ease",
          }}
        >
          {sidebarContent}
        </aside>
      </>
    );
  }

  return (
    <aside
      style={{
        width: open ? SIDEBAR_WIDTH : 0,
        minWidth: open ? SIDEBAR_WIDTH : 0,
        background: "#0A0F1C",
        borderRight: open ? `1px solid ${COLORS.border}` : "none",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "width 0.25s ease, min-width 0.25s ease",
        flexShrink: 0,
        height: "100vh",
        position: "sticky",
        top: 0,
      }}
    >
      <div style={{ width: SIDEBAR_WIDTH, display: "flex", flexDirection: "column", height: "100%" }}>
        {sidebarContent}
      </div>
    </aside>
  );
}

function PanelModal({ title, onClose, children, maxWidth = 480 }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8,12,24,0.85)",
        backdropFilter: "blur(12px)",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0D1224",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 20,
          padding: "28px 32px",
          width: "100%",
          maxWidth,
          maxHeight: "85vh",
          overflowY: "auto",
          animation: "modalPop 0.35s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 22, fontWeight: 600 }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: COLORS.muted,
              fontSize: 22,
              cursor: "pointer",
              lineHeight: 1,
              padding: 4,
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ProfileRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
      <span style={{ fontSize: 14, color: COLORS.muted }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 500, textAlign: "right" }}>{value}</span>
    </div>
  );
}

function ProfilePanel({ user, isPaid, credits, onClose, onUpgrade, onLogin, onRegister }) {
  if (!user) {
    return (
      <PanelModal title="Profile" onClose={onClose}>
        <div style={{ textAlign: "center", padding: "12px 0 24px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <UserAvatar name="Guest" size={64} />
          </div>
          <p style={{ color: COLORS.muted, fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
            Sign in to view your profile, save validation history, and manage your plan.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={onLogin} style={{ ...authButtonStyle, flex: 1 }}>Log in</button>
            <button
              type="button"
              onClick={onRegister}
              style={{
                flex: 1,
                padding: "14px 20px",
                borderRadius: 10,
                border: `1px solid ${COLORS.border}`,
                background: "transparent",
                color: COLORS.text,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Sign up
            </button>
          </div>
        </div>
      </PanelModal>
    );
  }

  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "—";

  return (
    <PanelModal title="Profile" onClose={onClose}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <UserAvatar name={user.name} size={72} />
        </div>
        <div style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 20, fontWeight: 600 }}>{user.name}</div>
        <div style={{ fontSize: 14, color: COLORS.muted, marginTop: 4 }}>{user.email}</div>
      </div>

      <ProfileRow label="Plan" value={planLabel(user.plan)} />
      <ProfileRow label="Credits" value={isPaid ? "Unlimited" : `${credits} remaining`} />
      <ProfileRow label="Member since" value={memberSince} />

      {!isPaid && (
        <button type="button" onClick={onUpgrade} style={{ ...authButtonStyle, marginTop: 24 }}>
          Upgrade plan
        </button>
      )}
    </PanelModal>
  );
}

function SettingToggle({ label, description, checked, onChange }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        padding: "14px 0",
        borderBottom: `1px solid ${COLORS.border}`,
        cursor: "pointer",
      }}
    >
      <span>
        <span style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{label}</span>
        <span style={{ display: "block", fontSize: 13, color: COLORS.muted, lineHeight: 1.5 }}>{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, accentColor: COLORS.indigo, flexShrink: 0, marginTop: 2 }}
      />
    </label>
  );
}

function SettingsPanel({ settings, onChange, onClose }) {
  return (
    <PanelModal title="Settings" onClose={onClose}>
      <SettingToggle
        label="Show example ideas"
        description="Display suggestion chips below the input field."
        checked={settings.showExamples}
        onChange={(v) => onChange({ ...settings, showExamples: v })}
      />
      <SettingToggle
        label="Product updates"
        description="Get occasional emails about new features and improvements."
        checked={settings.emailUpdates}
        onChange={(v) => onChange({ ...settings, emailUpdates: v })}
      />

      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>
          Keyboard shortcuts
        </div>
        <div style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
            <span>Validate idea</span>
            <span style={{ color: COLORS.text }}>⌘ / Ctrl + Enter</span>
          </div>
        </div>
      </div>
    </PanelModal>
  );
}

function AuthModal({ mode: initialMode, initialError = "", onClose, onSuccess }) {
  const [mode, setMode] = useState(initialMode || "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [error, setError] = useState(initialError);

  useEffect(() => {
    authApi
      .googleStatus()
      .then((data) => setGoogleEnabled(Boolean(data.enabled)))
      .catch(() => setGoogleEnabled(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data =
        mode === "register"
          ? await authApi.register({ name, email, password })
          : await authApi.login({ email, password });
      authApi.setToken(data.token);
      onSuccess(data.user);
      onClose();
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8,12,24,0.85)",
        backdropFilter: "blur(12px)",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0D1224",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 20,
          padding: 36,
          width: "100%",
          maxWidth: 420,
          animation: "modalPop 0.35s ease",
        }}
      >
        <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 24, marginBottom: 8, textAlign: "center" }}>
          {mode === "register" ? "Sign up" : "Log in"}
        </h2>
        <p style={{ color: COLORS.muted, fontSize: 14, textAlign: "center", marginBottom: 24 }}>
          {mode === "register"
            ? "Create an account — start with 3 free validations."
            : "Log in to validate ideas and view your history."}
        </p>

        <form onSubmit={submit}>
          {mode === "register" && (
            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={authInputStyle}
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={authInputStyle}
          />
          <input
            type="password"
            placeholder="Password (min. 6 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            style={authInputStyle}
          />

          {error && (
            <p style={{ color: COLORS.red, fontSize: 13, marginBottom: 12, textAlign: "center" }}>{error}</p>
          )}

          <button type="submit" disabled={loading} style={authButtonStyle}>
            {loading ? "Please wait…" : mode === "register" ? "Create account" : "Log in"}
          </button>
        </form>

        {googleEnabled && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                margin: "20px 0 16px",
                color: COLORS.muted,
                fontSize: 13,
              }}
            >
              <span style={{ flex: 1, height: 1, background: COLORS.border }} />
              or
              <span style={{ flex: 1, height: 1, background: COLORS.border }} />
            </div>
            <button
              type="button"
              disabled={loading}
              onClick={() => authApi.startGoogleLogin()}
              style={googleButtonStyle}
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Continue with Google
            </button>
          </>
        )}

        <p style={{ textAlign: "center", color: COLORS.muted, fontSize: 14, marginTop: 20 }}>
          {mode === "register" ? "Already have an account?" : "Don't have an account?"}{" "}
          <button
            type="button"
            onClick={() => {
              setMode(mode === "register" ? "login" : "register");
              setError("");
            }}
            style={{ background: "none", border: "none", color: COLORS.indigo, cursor: "pointer", fontSize: 14 }}
          >
            {mode === "register" ? "Log in" : "Sign up"}
          </button>
        </p>

        <button
          onClick={onClose}
          style={{
            display: "block",
            margin: "16px auto 0",
            background: "none",
            border: "none",
            color: COLORS.muted,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

const authInputStyle = {
  width: "100%",
  padding: "12px 14px",
  marginBottom: 12,
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "rgba(255,255,255,0.04)",
  color: COLORS.text,
  fontSize: 15,
  outline: "none",
  boxSizing: "border-box",
};

const authButtonStyle = {
  width: "100%",
  padding: "14px 20px",
  borderRadius: 10,
  border: "none",
  background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
  color: "#fff",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};

const googleButtonStyle = {
  width: "100%",
  padding: "12px 20px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "rgba(255,255,255,0.04)",
  color: COLORS.text,
  fontSize: 15,
  fontWeight: 500,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
};

function PaywallModal({ onClose, trigger, onPurchase, purchasing, purchaseError }) {
  const [selected, setSelected] = useState("pro");
  const plan = PLANS.find((p) => p.id === selected);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8,12,24,0.85)",
        backdropFilter: "blur(12px)",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0D1224",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 24,
          padding: 40,
          maxWidth: 900,
          width: "100%",
          animation: "modalPop 0.35s ease",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 28, marginBottom: 8 }}>
            {trigger === "out_of_credits" ? "You're out of free validations" : `Upgrade ${APP_NAME}`}
          </h2>
          <p style={{ color: COLORS.muted, fontSize: 15 }}>
            Pick a plan to keep validating startup ideas with AI.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 28 }}>
          {PLANS.map((p) => (
            <div
              key={p.id}
              onClick={() => setSelected(p.id)}
              style={{
                position: "relative",
                background: COLORS.surface,
                border: `2px solid ${selected === p.id ? p.color : COLORS.border}`,
                borderRadius: 16,
                padding: 24,
                cursor: "pointer",
                transition: "border-color 0.2s",
              }}
            >
              {p.popular && (
                <div
                  style={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: p.color,
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "4px 12px",
                    borderRadius: 99,
                  }}
                >
                  Most Popular
                </div>
              )}
              {selected === p.id && (
                <div
                  style={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: p.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    color: "#fff",
                  }}
                >
                  ✓
                </div>
              )}
              <div style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
                {p.name}
              </div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 32, fontWeight: 700, fontFamily: "Space Grotesk, sans-serif" }}>{p.price}</span>
                <span style={{ color: COLORS.muted, fontSize: 14 }}>{p.period}</span>
              </div>
              <div style={{ color: p.color, fontSize: 13, marginBottom: 16 }}>{p.credits}</div>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {p.features.map((f) => (
                  <li key={f} style={{ fontSize: 13, color: COLORS.muted, marginBottom: 8, display: "flex", gap: 8 }}>
                    <span style={{ color: p.color }}>✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {purchaseError && (
          <p style={{ color: COLORS.red, fontSize: 13, textAlign: "center", marginBottom: 12 }}>{purchaseError}</p>
        )}

        <button
          onClick={() => onPurchase(selected)}
          disabled={purchasing}
          style={{
            width: "100%",
            padding: "16px 24px",
            borderRadius: 12,
            border: "none",
            background: `linear-gradient(135deg, ${plan.color}, ${COLORS.violet})`,
            color: "#fff",
            fontSize: 16,
            fontWeight: 600,
            cursor: purchasing ? "wait" : "pointer",
            marginBottom: 16,
            opacity: purchasing ? 0.7 : 1,
          }}
        >
          {purchasing ? "Processing…" : `Continue to payment — ${plan.price}${plan.period}`}
        </button>

        <p style={{ textAlign: "center", color: COLORS.muted, fontSize: 13 }}>
          Secure in-app card payment · Plan activates after successful payment
        </p>

        <button
          onClick={onClose}
          style={{
            display: "block",
            margin: "20px auto 0",
            background: "none",
            border: "none",
            color: COLORS.muted,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}

function normalizeStoredSession(session) {
  const title = computeSessionTitle(session, {});
  if (title === session.title) return session;
  return { ...session, title };
}

function getInitialAppState() {
  const saved = loadHistory().map(normalizeStoredSession);
  const sessions = saved.length > 0 ? saved : [createSession()];
  const active = sessions[0];
  return {
    sessions,
    activeId: active.id,
    idea: active.idea || "",
    result: active.result || null,
  };
}

export default function App() {
  const initial = getInitialAppState();
  const [sessions, setSessions] = useState(initial.sessions);
  const [activeId, setActiveId] = useState(initial.activeId);
  const [idea, setIdea] = useState(initial.idea);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(initial.result);
  const [error, setError] = useState("");
  const [animateScores, setAnimateScores] = useState(false);
  const [phase, setPhase] = useState(0);
  const [textDone, setTextDone] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [authCallbackError, setAuthCallbackError] = useState("");
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState("manual");
  const [purchaseError, setPurchaseError] = useState("");
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const [settings, setSettings] = useState(loadSettings);
  const [libraryFilter, setLibraryFilter] = useState("all");
  const [showLibrary, setShowLibrary] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return localStorage.getItem("vcscore_sidebar") !== "closed";
    } catch {
      return true;
    }
  });
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);

  const phaseIntervalRef = useRef(null);

  const credits = user?.credits ?? 0;
  const isPaid = user?.isUnlimited ?? false;

  const applyUser = useCallback((u, { openAdmin = false } = {}) => {
    setUser(u);
    if (u?.isAdmin && openAdmin) {
      setActivePanel("admin");
    }
  }, []);

  const loadUserSessions = useCallback(async () => {
    try {
      const data = await validationApi.list();
      if (data.sessions?.length > 0) {
        setSessions(data.sessions);
        const first = data.sessions[0];
        setActiveId(first.id);
        setIdea(first.idea || "");
        setResult(first.result || null);
      } else {
        const session = createSession();
        setSessions([session]);
        setActiveId(session.id);
        setIdea("");
        setResult(null);
      }
    } catch {
      /* keep local sessions */
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get("auth_token");
    const authError = params.get("auth_error");

    if (authToken) {
      authApi.setToken(authToken);
      setShowAuth(false);
      setAuthCallbackError("");
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (authError) {
      setAuthMode("login");
      setShowAuth(true);
      setAuthCallbackError(decodeURIComponent(authError));
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (params.get("checkout") === "success" && authApi.getToken()) {
      authApi.me().then((data) => applyUser(data.user));
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (params.get("checkout") === "cancel") {
      window.history.replaceState({}, "", window.location.pathname);
    }

    const token = authApi.getToken();
    if (!token) {
      setAuthLoading(false);
      return;
    }

    authApi
      .me()
      .then((data) => {
        applyUser(data.user);
        return loadUserSessions();
      })
      .catch(() => authApi.logout())
      .finally(() => setAuthLoading(false));
  }, [applyUser, loadUserSessions]);

  const openAuth = useCallback((mode = "login") => {
    setAuthCallbackError("");
    setAuthMode(mode);
    setShowAuth(true);
  }, []);

  const openPanel = useCallback((panel) => {
    setActivePanel(panel);
  }, []);

  const handleSettingsChange = useCallback((next) => {
    setSettings(next);
    saveSettings(next);
  }, []);

  const handleLogout = useCallback(() => {
    authApi.logout();
    setUser(null);
    const session = createSession();
    setSessions([session]);
    setActiveId(session.id);
    setIdea("");
    setResult(null);
    setError("");
  }, []);

  const handlePurchase = useCallback(
    (planId) => {
      if (!user) {
        openAuth("login");
        return;
      }
      const plan = PLANS.find((p) => p.id === planId);
      if (!plan) return;
      setPurchaseError("");
      setShowPaywall(false);
      setPaymentPlan({
        id: plan.id,
        name: plan.name,
        priceCents: plan.priceCents,
        period: plan.period,
      });
    },
    [user, openAuth]
  );

  const handlePaymentSuccess = useCallback(
    (updatedUser) => {
      applyUser(updatedUser);
      setPaymentPlan(null);
      setShowPaywall(false);
    },
    [applyUser]
  );

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!user) saveHistory(sessions);
  }, [sessions, user]);

  useEffect(() => {
    try {
      localStorage.setItem("vcscore_sidebar", sidebarOpen ? "open" : "closed");
    } catch {
      /* ignore */
    }
  }, [sidebarOpen]);

  const openPaywall = useCallback((trigger = "manual") => {
    setPaywallTrigger(trigger);
    setShowPaywall(true);
  }, []);

  const persistActiveSession = useCallback((updates) => {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === activeId
          ? {
              ...s,
              ...updates,
              title: computeSessionTitle(s, updates),
              updatedAt: Date.now(),
            }
          : s
      )
    );
  }, [activeId]);

  const selectSession = useCallback((id) => {
    const session = sessions.find((s) => s.id === id);
    if (!session) return;
    setActiveId(id);
    setIdea(session.idea || "");
    setResult(session.result || null);
    setError("");
    setAnimateScores(!!session.result);
    setTextDone(false);
    setPhase(0);
    if (isMobile) setSidebarOpen(false);
  }, [sessions, isMobile]);

  const newSession = useCallback(() => {
    const session = createSession();
    setSessions((prev) => [session, ...prev]);
    setActiveId(session.id);
    setIdea("");
    setResult(null);
    setError("");
    setAnimateScores(false);
    setTextDone(false);
    setPhase(0);
    if (isMobile) setSidebarOpen(false);
  }, [isMobile]);

  const deleteSession = useCallback(async (id) => {
    const isDbId = /^\d+$/.test(String(id));
    if (user && isDbId) {
      try {
        await validationApi.delete(id);
      } catch (err) {
        setError(err.message || "Failed to delete");
        return;
      }
    }

    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (next.length === 0) {
        const fresh = createSession();
        setActiveId(fresh.id);
        setIdea("");
        setResult(null);
        setError("");
        setAnimateScores(false);
        return [fresh];
      }
      if (id === activeId) {
        const first = next[0];
        setActiveId(first.id);
        setIdea(first.idea || "");
        setResult(first.result || null);
        setError("");
        setAnimateScores(!!first.result);
      }
      return next;
    });
  }, [activeId, user]);

  const toggleStar = useCallback(async (id) => {
    const isDbId = /^\d+$/.test(String(id));

    if (user && isDbId) {
      try {
        const data = await validationApi.toggleStar(id);
        setSessions((prev) =>
          prev.map((s) => (s.id === id ? { ...s, ...data.session, result: s.result || data.session.result } : s))
        );
      } catch (err) {
        setError(err.message || "Failed to update favorite");
      }
      return;
    }

    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, starred: !s.starred } : s)));
  }, [user]);

  const resetForm = () => {
    newSession();
  };

  const validateIdea = async () => {
    if (!idea.trim()) return;

    const guestDemo = !user;
    if (guestDemo && demoUsesRemaining() <= 0) {
      openAuth("register");
      setError("You've used your free demos. Create a free account for live AI validations.");
      return;
    }

    if (user && !isPaid && credits <= 0) {
      openPaywall("out_of_credits");
      return;
    }

    setLoading(true);
    setResult(null);
    setError("");
    setAnimateScores(false);
    setTextDone(false);
    setPhase(0);

    persistActiveSession({ idea, result: null });

    phaseIntervalRef.current = setInterval(() => {
      setPhase((p) => (p + 1) % PHASE_LABELS.length);
    }, 900);

    try {
      const data = guestDemo
        ? await validationApi.demoValidate(idea)
        : await validationApi.validate(idea);
      const parsed =
        typeof data.result === "string"
          ? JSON.parse(data.result.replace(/```json|```/g, "").trim())
          : data.result;

      const nextResult = guestDemo || data.demo ? { ...parsed, demo: true } : parsed;
      setResult(nextResult);

      if (guestDemo) {
        incrementDemoUses();
      }

      if (data.user) {
        applyUser(data.user);
      }

      if (data.session) {
        setSessions((prev) => {
          const exists = prev.some((s) => s.id === data.session.id);
          if (exists) {
            return prev.map((s) => (s.id === data.session.id ? { ...s, ...data.session, result: nextResult } : s));
          }
          return [data.session, ...prev].slice(0, 50);
        });
        setActiveId(data.session.id);
      } else {
        persistActiveSession({ idea, result: nextResult });
      }

      setTimeout(() => setAnimateScores(true), 400);
    } catch (err) {
      if (err.status === 401) {
        authApi.logout();
        setUser(null);
        openAuth("login");
        setError("Please log in to validate.");
      } else if (err.status === 402) {
        openPaywall("out_of_credits");
        setError(err.message);
      } else if (err.status === 429) {
        openAuth("register");
        setError(err.message || "Too many demos from this network. Sign up for live AI validations.");
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      clearInterval(phaseIntervalRef.current);
      setLoading(false);
    }
  };

  const handleIdeaChange = (e) => {
    const val = e.target.value;
    setIdea(val);
    persistActiveSession({ idea: val, result });
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      validateIdea();
    }
  };

  const toggleSidebar = () => setSidebarOpen((o) => !o);

  const scrollToValidate = useCallback(() => {
    document.getElementById("validate")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const overallScore = result ? getOverallScore(result.scores) : 0;
  const verdictColor = result ? getVerdictColor(result.verdict) : COLORS.indigo;
  const outOfCredits = Boolean(user) && !isPaid && credits <= 0;
  const isDemoResult = Boolean(result?.demo);
  const demosLeft = demoUsesRemaining();
  const activeSession = sessions.find((s) => s.id === activeId);
  const isStarred = Boolean(activeSession?.starred);

  return (
    <div style={{ minHeight: "100vh", display: "flex", position: "relative" }}>
      <style>{GLOBAL_STYLES}</style>

      <Sidebar
        open={sidebarOpen}
        onToggle={toggleSidebar}
        sessions={sessions}
        activeId={activeId}
        onSelect={selectSession}
        onNew={newSession}
        onDelete={deleteSession}
        credits={credits}
        isPaid={isPaid}
        onUpgrade={() => openPaywall("manual")}
        isMobile={isMobile}
        user={user}
        onLogin={() => openAuth("login")}
        onRegister={() => openAuth("register")}
        onLogout={handleLogout}
        onOpenPanel={openPanel}
        onOpenLibrary={() => setShowLibrary(true)}
        libraryFilter={libraryFilter}
        onLibraryFilterChange={setLibraryFilter}
        onToggleStar={toggleStar}
      />

      <div style={{ flex: 1, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>
        <OrbBg />

        {!sidebarOpen && (
          <button
            onClick={toggleSidebar}
            title="Open sidebar"
            style={{
              position: "fixed",
              top: 14,
              left: 14,
              zIndex: 50,
              background: "rgba(10,15,28,0.9)",
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              color: COLORS.muted,
              cursor: "pointer",
              padding: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(8px)",
            }}
          >
            <IconMenu />
          </button>
        )}

        <div style={{ position: "relative", zIndex: 1, width: "100%" }}>
        {!user && !result && !loading && (
          <LandingPage
            part="hero"
            onValidate={scrollToValidate}
            onSignup={() => openAuth("register")}
            onPricing={() => openPaywall("manual")}
          />
        )}

        <section
          id="validate"
          style={{ padding: "24px 24px 80px", width: "100%" }}
        >
        {(user || result || loading) && (
        <header style={{ textAlign: "center", marginBottom: 40, animation: "fadeUp 0.6s ease", paddingTop: sidebarOpen && !isMobile ? 0 : 40 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 99,
              padding: "6px 16px",
              marginBottom: 24,
              fontSize: 13,
              color: COLORS.muted,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: COLORS.green,
                animation: "pulse 2s infinite",
              }}
            />
            AI Venture Partner · Live
          </div>
          <h1 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "clamp(36px, 6vw, 52px)", fontWeight: 700, lineHeight: 1.15, marginBottom: 16 }}>
            Would a VC
            <br />
            <span
              style={{
                background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              fund your idea?
            </span>
          </h1>
          <p style={{ color: COLORS.muted, fontSize: 16 }}>
            {authLoading
              ? "Loading…"
              : !user
                ? demosLeft > 0
                  ? `Try ${demosLeft} free demo validation${demosLeft !== 1 ? "s" : ""} — no signup needed.`
                  : "Demos used — create a free account for live AI validations."
                : isPaid
                  ? "Unlimited validations with your Pro plan."
                  : `${credits} free validation${credits !== 1 ? "s" : ""} remaining.`}
          </p>
        </header>
        )}

        {/* Input card */}
        <div
          style={{
            background: COLORS.surface,
            border: `1px solid ${loading ? COLORS.indigo : COLORS.border}`,
            borderRadius: 20,
            padding: 6,
            marginBottom: 20,
            boxShadow: loading ? `0 0 40px rgba(99,102,241,0.15)` : "none",
            transition: "border-color 0.3s, box-shadow 0.3s",
            animation: "fadeUp 0.6s ease 0.1s both",
            maxWidth: "100%",
          }}
        >
          <textarea
            value={idea}
            onChange={handleIdeaChange}
            onKeyDown={handleKeyDown}
            rows={!result && !loading && !isMobile ? 6 : 5}
            placeholder="Describe your startup idea in a few sentences…"
            disabled={loading}
            style={{
              width: "100%",
              background: "rgba(0,0,0,0.3)",
              border: "none",
              borderRadius: 16,
              padding: "16px 18px",
              color: COLORS.text,
              fontSize: 15,
              lineHeight: 1.6,
              resize: "vertical",
              outline: "none",
              fontFamily: "Inter, sans-serif",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px 12px" }}>
            <div style={{ fontSize: 12, color: COLORS.muted }}>
              {idea.length} chars · <span style={{ opacity: 0.7 }}>⌘↵ to validate</span>
            </div>
            <button
              onClick={validateIdea}
              disabled={loading || !idea.trim()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: outOfCredits
                  ? "rgba(255,255,255,0.08)"
                  : `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
                border: "none",
                borderRadius: 12,
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                padding: "10px 22px",
                cursor: loading || !idea.trim() ? "not-allowed" : "pointer",
                opacity: !idea.trim() ? 0.5 : 1,
              }}
            >
              {loading ? (
                <>
                  <svg width={16} height={16} viewBox="0 0 24 24" style={{ animation: "spin 0.8s linear infinite" }}>
                    <circle cx={12} cy={12} r={10} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={3} />
                    <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" />
                  </svg>
                  Validating…
                </>
              ) : outOfCredits ? (
                "🔒 Upgrade to validate"
              ) : !user ? (
                demosLeft > 0 ? "Try free demo" : "Sign up to validate"
              ) : (
                "Validate Idea"
              )}
            </button>
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "rgba(239,68,68,0.1)",
              border: `1px solid rgba(239,68,68,0.3)`,
              borderRadius: 12,
              padding: "14px 18px",
              marginBottom: 20,
              color: COLORS.red,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {/* Example chips */}
        {settings.showExamples && !result && !loading && (!user || isPaid || credits > 0) && (
          <div style={{ marginBottom: 24, animation: "fadeUp 0.6s ease 0.2s both" }}>
            <p style={{ fontSize: 12, color: COLORS.muted, marginBottom: 10 }}>
              {!user ? "Try a demo example" : "Try an example"}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {EXAMPLE_CHIPS.map((chip) => (
                <button
                  key={chip}
                  onClick={() => setIdea(chip)}
                  style={{
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 99,
                    color: COLORS.muted,
                    fontSize: 13,
                    padding: "8px 16px",
                    cursor: "pointer",
                  }}
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Out of credits banner */}
        {user && credits === 0 && !result && !loading && !isPaid && (
          <div
            style={{
              background: "rgba(99,102,241,0.08)",
              border: `1px solid rgba(99,102,241,0.25)`,
              borderRadius: 16,
              padding: "20px 24px",
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              animation: "fadeUp 0.5s ease",
            }}
          >
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>You've used all your free validations</div>
              <div style={{ fontSize: 14, color: COLORS.muted }}>Upgrade to keep stress-testing your startup ideas.</div>
            </div>
            <button
              onClick={() => openPaywall("out_of_credits")}
              style={{
                background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
                border: "none",
                borderRadius: 10,
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                padding: "10px 20px",
                cursor: "pointer",
              }}
            >
              See Plans →
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ animation: "fadeUp 0.4s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
              <svg width={32} height={32} viewBox="0 0 24 24" style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
                <circle cx={12} cy={12} r={10} fill="none" stroke="rgba(99,102,241,0.2)" strokeWidth={3} />
                <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke={COLORS.indigo} strokeWidth={3} strokeLinecap="round" />
              </svg>
              <span style={{ fontSize: 15, color: COLORS.muted }}>{PHASE_LABELS[phase]}</span>
            </div>
            <LoadingCard />
            <LoadingCard />
            <LoadingCard />
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div style={{ animation: "fadeUp 0.5s ease" }}>
            {/* Verdict hero */}
            <div
              style={{
                position: "relative",
                overflow: "hidden",
                background: COLORS.surface,
                border: `1px solid ${verdictColor}33`,
                borderRadius: 20,
                padding: 32,
                marginBottom: 24,
                boxShadow: `0 0 60px ${verdictColor}22`,
                display: "flex",
                alignItems: "center",
                gap: 28,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(180deg, transparent 0%, ${verdictColor}08 50%, transparent 100%)`,
                  animation: "scanline 3s linear infinite",
                  pointerEvents: "none",
                }}
              />
              <div style={{ animation: "verdictPop 0.6s ease" }}>
                <BigRing score={overallScore} color={verdictColor} animate={animateScores} />
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div
                    style={{
                      fontFamily: "Space Grotesk, sans-serif",
                      fontSize: 28,
                      fontWeight: 700,
                      color: verdictColor,
                      marginBottom: 8,
                      animation: "verdictPop 0.6s ease 0.1s both",
                    }}
                  >
                    {result.verdict}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    {isDemoResult && (
                      <span
                        title="Built-in sample analysis — no AI tokens used"
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 0.8,
                          textTransform: "uppercase",
                          color: COLORS.indigo,
                          background: "rgba(99,102,241,0.12)",
                          border: "1px solid rgba(99,102,241,0.35)",
                          borderRadius: 99,
                          padding: "6px 12px",
                        }}
                      >
                        Demo
                      </span>
                    )}
                    {user && activeSession?.result && (
                      <button
                        type="button"
                        onClick={() => toggleStar(activeId)}
                        title={isStarred ? "Remove from favorites" : "Add to favorites"}
                        style={{
                          background: isStarred ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.04)",
                          border: `1px solid ${isStarred ? "rgba(245,158,11,0.35)" : COLORS.border}`,
                          borderRadius: 10,
                          color: isStarred ? "#F59E0B" : COLORS.muted,
                          cursor: "pointer",
                          padding: "8px 12px",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 13,
                        }}
                      >
                        <IconStar filled={isStarred} size={16} />
                        {isStarred ? "Favorited" : "Favorite"}
                      </button>
                    )}
                  </div>
                </div>
                <p style={{ fontSize: 16, color: COLORS.muted, lineHeight: 1.5, animation: "fadeUp 0.5s ease 0.2s both" }}>
                  {result.tagline}
                </p>
              </div>
            </div>

            {/* Score breakdown */}
            <div
              style={{
                background: COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 20,
                padding: 28,
                marginBottom: 20,
              }}
            >
              <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, marginBottom: 20, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase" }}>
                Score Breakdown
              </h3>
              {SCORE_DIMENSIONS.map((dim, i) => (
                <ScoreBar
                  key={dim.key}
                  score={result.scores[dim.key]}
                  label={dim.label}
                  icon={dim.icon}
                  delay={i * 120}
                  animate={animateScores}
                />
              ))}
            </div>

            {/* Bull case */}
            <div
              style={{
                background: COLORS.surface,
                border: `1px solid rgba(16,185,129,0.2)`,
                borderLeft: `3px solid ${COLORS.green}`,
                borderRadius: 16,
                padding: 24,
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.green, letterSpacing: 1, marginBottom: 10, textTransform: "uppercase" }}>
                Bull Case
              </div>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: COLORS.text }}>
                <TypingText text={result.bull_case} onDone={() => setTextDone(true)} />
              </p>
            </div>

            {/* Bear case */}
            <div
              style={{
                background: COLORS.surface,
                border: `1px solid rgba(239,68,68,0.2)`,
                borderLeft: `3px solid ${COLORS.red}`,
                borderRadius: 16,
                padding: 24,
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.red, letterSpacing: 1, marginBottom: 10, textTransform: "uppercase" }}>
                Bear Case
              </div>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: COLORS.text }}>{result.bear_case}</p>
            </div>

            {/* Killer insight */}
            <div
              style={{
                background: COLORS.surface,
                border: `1px solid rgba(245,158,11,0.2)`,
                borderLeft: `3px solid ${COLORS.amber}`,
                borderRadius: 16,
                padding: 24,
                marginBottom: 24,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.amber, letterSpacing: 1, marginBottom: 10, textTransform: "uppercase" }}>
                Killer Insight
              </div>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: COLORS.text, fontStyle: "italic" }}>{result.killer_insight}</p>
            </div>

            {(result.verdict === "NEEDS WORK" || result.verdict === "HARD PASS") && user && (
              <IdeaDevelopmentPanel
                validationId={activeId}
                idea={idea}
                result={result}
                userPlan={user.plan || "free"}
                onUsePivot={(pivotIdea) => {
                  setIdea(pivotIdea);
                  persistActiveSession({ idea: pivotIdea, result: null });
                  setResult(null);
                  setAnimateScores(false);
                  document.getElementById("validate")?.scrollIntoView({ behavior: "smooth" });
                }}
                onUpgrade={() => openPaywall("manual")}
              />
            )}

            {isDemoResult && !user && (
              <div
                style={{
                  background: "rgba(99,102,241,0.1)",
                  border: "1px solid rgba(99,102,241,0.35)",
                  borderRadius: 16,
                  padding: 24,
                  marginBottom: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                    This was a built-in demo
                  </div>
                  <p style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.5, margin: 0 }}>
                    No AI tokens were used. Sign up free for a live VC-style AI verdict
                    {demosLeft > 0 ? ` — you still have ${demosLeft} demo${demosLeft !== 1 ? "s" : ""} left.` : "."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openAuth("register")}
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
                    border: "none",
                    borderRadius: 12,
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    padding: "12px 22px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Get live AI validation →
                </button>
              </div>
            )}

            {/* Post-result upsell */}
            {!isPaid && user && credits <= 1 && (
              <div
                style={{
                  background: "rgba(99,102,241,0.08)",
                  border: `1px solid rgba(99,102,241,0.2)`,
                  borderRadius: 16,
                  padding: "18px 22px",
                  marginBottom: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 14, color: COLORS.muted }}>
                  {credits === 0 ? "No free validations left." : "Last free validation used."} Go unlimited with Pro.
                </span>
                <button
                  onClick={() => openPaywall("manual")}
                  style={{
                    background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
                    border: "none",
                    borderRadius: 10,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "8px 18px",
                    cursor: "pointer",
                  }}
                >
                  Upgrade →
                </button>
              </div>
            )}

            <button
              onClick={resetForm}
              style={{
                background: "none",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 12,
                color: COLORS.muted,
                fontSize: 14,
                padding: "12px 20px",
                cursor: "pointer",
              }}
            >
              ← Validate another idea
            </button>
          </div>
        )}
        </section>

        {!user && (
          <LandingPage
            part="rest"
            onValidate={scrollToValidate}
            onSignup={() => openAuth("register")}
            onPricing={() => openPaywall("manual")}
          />
        )}
        </div>

        {showPaywall && (
          <PaywallModal
            onClose={() => {
              setShowPaywall(false);
              setPurchaseError("");
            }}
            trigger={paywallTrigger}
            onPurchase={handlePurchase}
            purchasing={false}
            purchaseError={purchaseError}
          />
        )}
        {paymentPlan && (
          <PaymentModal
            planId={paymentPlan.id}
            planMeta={paymentPlan}
            onClose={() => setPaymentPlan(null)}
            onSuccess={handlePaymentSuccess}
          />
        )}
        {showAuth && (
          <AuthModal
            mode={authMode}
            initialError={authCallbackError}
            onClose={() => {
              setShowAuth(false);
              setAuthCallbackError("");
            }}
            onSuccess={(u) => {
              applyUser(u, { openAdmin: true });
              loadUserSessions();
              setAuthCallbackError("");
            }}
          />
        )}
        {activePanel === "admin" && user?.isAdmin && (
          <AdminPanel
            onClose={() => setActivePanel(null)}
            currentUserId={user.id}
            currentUserEmail={user.email}
          />
        )}
        {showLibrary && user && (
          <LibraryPanel
            sessions={sessions}
            onClose={() => setShowLibrary(false)}
            onSelect={selectSession}
            onToggleStar={toggleStar}
          />
        )}
        {activePanel === "profile" && (
          <ProfilePanel
            user={user}
            isPaid={isPaid}
            credits={credits}
            onClose={() => setActivePanel(null)}
            onUpgrade={() => {
              setActivePanel(null);
              openPaywall("manual");
            }}
            onLogin={() => {
              setActivePanel(null);
              openAuth("login");
            }}
            onRegister={() => {
              setActivePanel(null);
              openAuth("register");
            }}
          />
        )}
        {activePanel === "settings" && (
          <SettingsPanel
            settings={settings}
            onChange={handleSettingsChange}
            onClose={() => setActivePanel(null)}
          />
        )}
        {activePanel === "help" && (
          <HelpPanel onClose={() => setActivePanel(null)} onNavigate={openPanel} />
        )}
        {activePanel === "faq" && (
          <FaqPanel onClose={() => setActivePanel(null)} onNavigate={openPanel} />
        )}
        {activePanel === "release-notes" && <ReleaseNotesPanel onClose={() => setActivePanel(null)} />}
        {activePanel === "shortcuts" && <KeyboardShortcutsPanel onClose={() => setActivePanel(null)} />}
        {activePanel === "terms" && <TermsPanel onClose={() => setActivePanel(null)} />}
        {activePanel === "privacy" && <PrivacyPanel onClose={() => setActivePanel(null)} />}
        {activePanel === "report-bug" && (
          <ReportBugPanel
            user={user}
            onClose={() => setActivePanel(null)}
            onLogin={() => openAuth("login")}
          />
        )}
      </div>
    </div>
  );
}
